/**
 * Product Showcase Hero — JavaScript
 *
 * 1. VariantCounterPicker: custom element for −/+ counter-based variant selection.
 *    Clamps counters to valid combinations only.
 * 2. Buy Now handler: submits to /cart/add.js then redirects to /checkout.
 */

/* ==========================================
   Variant Counter Picker
   ========================================== */

if (!customElements.get('variant-counter-picker')) {
  class VariantCounterPicker extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;

      /* --- Load variant data from JSON script tag --- */
      const dataEl = document.getElementById('psh-data-' + this.sectionId);
      if (!dataEl) return;

      const data = JSON.parse(dataEl.textContent);
      this.variants = data.variants;
      this.optionNames = data.option_names;
      this.moneyFormat = data.money_format || '${{amount}}';

      /* --- Parse unique sorted values per option --- */
      this.optionValues = [];
      for (let i = 0; i < this.optionNames.length; i++) {
        const seen = new Set();
        const values = [];
        this.variants.forEach(function (v) {
          if (!seen.has(v.options[i])) {
            seen.add(v.options[i]);
            values.push(v.options[i]);
          }
        });
        values.sort(function (a, b) {
          var numA = parseFloat(a);
          var numB = parseFloat(b);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return String(a).localeCompare(String(b));
        });
        this.optionValues.push(values);
      }

      /* --- Determine current indices from selected variant --- */
      this.counterValueEls = this.querySelectorAll('.psh__counter-value');
      this.currentIndices = [];

      var selectedVariant = null;
      for (var vi = 0; vi < this.variants.length; vi++) {
        if (this.variants[vi].id === data.selected_variant_id) {
          selectedVariant = this.variants[vi];
          break;
        }
      }

      for (var oi = 0; oi < this.optionNames.length; oi++) {
        if (selectedVariant) {
          this.currentIndices[oi] = this.optionValues[oi].indexOf(selectedVariant.options[oi]);
        } else {
          this.currentIndices[oi] = 0;
        }
      }

      /* --- Build variant lookup map: "opt0|opt1|..." -> variant --- */
      this.variantMap = {};
      this.variants.forEach(function (v) {
        this.variantMap[v.options.join('|')] = v;
      }.bind(this));

      /* --- Bind click events --- */
      this.querySelectorAll('.psh__counter-btn').forEach(function (btn) {
        btn.addEventListener('click', this._onCounterClick.bind(this));
      }.bind(this));

      /* --- Set initial button disabled states --- */
      this._updateCounterButtons();
    }

    /* --- Event: counter button clicked --- */
    _onCounterClick(e) {
      var btn = e.currentTarget;
      var action = btn.dataset.action;
      var optIdx = parseInt(btn.dataset.optionIndex, 10);
      var currentIdx = this.currentIndices[optIdx];
      var values = this.optionValues[optIdx];

      var newIdx = action === 'increment' ? currentIdx + 1 : currentIdx - 1;
      if (newIdx < 0 || newIdx >= values.length) return;

      /* Check if new combo is valid */
      var testOptions = this._getCurrentOptions();
      testOptions[optIdx] = values[newIdx];
      var testKey = testOptions.join('|');

      if (this.variantMap[testKey]) {
        /* Direct match — just update this option */
        this.currentIndices[optIdx] = newIdx;
        this.counterValueEls[optIdx].textContent = values[newIdx];
      } else {
        /* No direct match — adjust other options to find closest valid combo */
        var adjusted = this._findValidCombination(optIdx, values[newIdx]);
        if (!adjusted) return;

        for (var i = 0; i < adjusted.length; i++) {
          this.currentIndices[i] = this.optionValues[i].indexOf(adjusted[i]);
          if (this.counterValueEls[i]) {
            this.counterValueEls[i].textContent = adjusted[i];
          }
        }
      }

      this._updateVariant();
      this._updateCounterButtons();
    }

    /* --- Get current option values as array --- */
    _getCurrentOptions() {
      var opts = [];
      for (var i = 0; i < this.currentIndices.length; i++) {
        opts.push(this.optionValues[i][this.currentIndices[i]]);
      }
      return opts;
    }

    /* --- Find the closest valid combination when changing one option --- */
    _findValidCombination(changedIdx, newValue) {
      /* Get all variants that match the new value for the changed option */
      var matching = this.variants.filter(function (v) {
        return v.options[changedIdx] === newValue;
      });
      if (matching.length === 0) return null;

      /* Find the variant closest to the current selection for other options */
      var bestVariant = null;
      var bestDist = Infinity;

      for (var mi = 0; mi < matching.length; mi++) {
        var dist = 0;
        for (var oi = 0; oi < this.optionNames.length; oi++) {
          if (oi === changedIdx) continue;
          var currentVal = this.optionValues[oi][this.currentIndices[oi]];
          var variantVal = matching[mi].options[oi];
          var numCurrent = parseFloat(currentVal);
          var numVariant = parseFloat(variantVal);
          if (!isNaN(numCurrent) && !isNaN(numVariant)) {
            dist += Math.abs(numCurrent - numVariant);
          } else if (currentVal !== variantVal) {
            dist += 1;
          }
        }
        if (dist < bestDist) {
          bestDist = dist;
          bestVariant = matching[mi];
        }
      }

      return bestVariant ? bestVariant.options.slice() : null;
    }

    /* --- Update form inputs, price, URL and button availability --- */
    _updateVariant() {
      var options = this._getCurrentOptions();
      var key = options.join('|');
      var variant = this.variantMap[key];
      if (!variant) return;

      var section = document.querySelector('[data-section-id="' + this.sectionId + '"]');
      if (!section) return;

      /* Update hidden inputs in all forms */
      section.querySelectorAll('input[name="id"]').forEach(function (input) {
        input.value = variant.id;
      });

      /* Update price displays (mobile + desktop) */
      this._updatePrice(variant, section);

      /* Update button states */
      var addBtn = section.querySelector('.psh__btn--add');
      var buyBtn = section.querySelector('.psh__btn--buy');

      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available
          ? (addBtn.dataset.addText || 'Agregar al carrito')
          : (addBtn.dataset.soldText || 'Agotado');
      }
      if (buyBtn) {
        buyBtn.disabled = !variant.available;
      }

      /* Update URL */
      if (this.productUrl && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    /* --- Update all price containers --- */
    _updatePrice(variant, section) {
      var self = this;
      var containers = section.querySelectorAll('.psh__price, .psh__col-price');

      containers.forEach(function (container) {
        var currentEl = container.querySelector('.psh__price-current');
        var compareEl = container.querySelector('.psh__price-compare');

        if (currentEl) {
          currentEl.textContent = self._formatMoney(variant.price);
        }

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          if (!compareEl) {
            compareEl = document.createElement('s');
            compareEl.className = 'psh__price-compare';
            currentEl.parentNode.insertBefore(compareEl, currentEl);
          }
          compareEl.textContent = self._formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else if (compareEl) {
          compareEl.style.display = 'none';
        }
      });
    }

    /* --- Enable/disable counter buttons based on validity --- */
    _updateCounterButtons() {
      var self = this;
      this.querySelectorAll('.psh__counter-btn').forEach(function (btn) {
        var action = btn.dataset.action;
        var optIdx = parseInt(btn.dataset.optionIndex, 10);
        var currentIdx = self.currentIndices[optIdx];
        var values = self.optionValues[optIdx];
        var shouldDisable = false;

        if (action === 'increment') {
          if (currentIdx >= values.length - 1) {
            shouldDisable = true;
          } else {
            /* Check if ANY variant exists with the next value */
            var nextVal = values[currentIdx + 1];
            var exists = self.variants.some(function (v) {
              return v.options[optIdx] === nextVal;
            });
            shouldDisable = !exists;
          }
        } else {
          if (currentIdx <= 0) {
            shouldDisable = true;
          } else {
            var prevVal = values[currentIdx - 1];
            var exists = self.variants.some(function (v) {
              return v.options[optIdx] === prevVal;
            });
            shouldDisable = !exists;
          }
        }

        btn.disabled = shouldDisable;
      });
    }

    /* --- Format price from cents to money string --- */
    _formatMoney(cents) {
      if (cents == null) return '';
      var amount = (cents / 100).toFixed(2);
      var amountNoDecimals = Math.floor(cents / 100).toString();
      var amountFormatted = amount.replace(/\B(?=(\d{3})+\.)/g, ',');
      var amountNoDecimalsFormatted = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var amountWithComma = amount.replace('.', ',');
      var amountWithCommaThousands = amountWithComma.replace(/\B(?=(\d{3})+,)/g, '.');

      return this.moneyFormat
        .replace('{{amount_with_comma_separator}}', amountWithCommaThousands)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
        .replace('{{amount_no_decimals}}', amountNoDecimalsFormatted)
        .replace('{{amount}}', amountFormatted);
    }
  }

  customElements.define('variant-counter-picker', VariantCounterPicker);
}

/* ==========================================
   Buy Now Handler
   Adds to cart then redirects to /checkout
   ========================================== */

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.psh__btn--buy').forEach(function (btn) {
    var form = btn.closest('form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var originalText = btn.textContent;
      btn.disabled = true;
      btn.classList.add('psh__btn--loading');

      var variantId = form.querySelector('input[name="id"]').value;
      var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(cartRoot + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: 'id=' + encodeURIComponent(variantId) + '&quantity=1'
      })
        .then(function (res) {
          if (res.ok) {
            window.location.href = cartRoot + 'checkout';
          } else {
            return res.json().then(function (data) {
              alert(data.description || 'No se pudo agregar el producto.');
              btn.disabled = false;
              btn.classList.remove('psh__btn--loading');
              btn.textContent = originalText;
            });
          }
        })
        .catch(function () {
          alert('Error de conexión. Intenta de nuevo.');
          btn.disabled = false;
          btn.classList.remove('psh__btn--loading');
          btn.textContent = originalText;
        });
    });
  });
});
